import React, {useCallback, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import ResizeDetector from 'react-resize-detector';
import {
	FixedSizeList,
	type FixedSizeListProps,
	type ListChildComponentProps,
	type ListOnItemsRenderedProps,
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import _ from 'underscore';
import useEnhancedRef from '@netx/core-components/hooks/useEnhancedRef';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import {ScrollBorderReactWindow} from '@netx/core-components/ScrollBorder';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {getScrollbarWidth} from '@netx/core-next/utils/browser/getScrollbarWidth';
import {combineRefs} from '@netx/core-next/utils/react';
import {VirtualizedListClassicPager, VirtualizedListPager} from '../';
import type {
	BaseItemData,
	InfiniteLoaderComponentCommonProps,
} from '../../types';
import {
	getVirtualizedListClass,
	virtualizedListClasses,
	VirtualizedListClasses,
} from './virtualizedListClasses';

type VirtualizedListScrollBorderUtilityClassProps = WithClasses<
	VirtualizedListClasses,
	{
		showBorder?: boolean;
	}
>;

const useUtilityClasses = (
	ownerState: VirtualizedListScrollBorderUtilityClassProps,
) => {
	const {classes, showBorder} = ownerState;

	const slots = {
		root: ['root', showBorder && 'showBorder'],
		footer: ['footer'],
		wrapper: ['wrapper'],
	};

	return composeClasses(slots, getVirtualizedListClass, classes);
};

const VirtualizedListRoot = styled('div', {
	name: 'VirtualizedList',
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	overflow: 'hidden',
	position: 'relative',
	minHeight: 0,
}));

const VirtualizedListFooter = styled('div', {
	name: 'VirtualizedList',
	slot: 'Footer',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	padding: theme.spacing(1),
	alignItems: 'center',
	// justifyContent: 'flex-end',
}));

const VirtualizedListWrapper = styled('div', {
	name: 'VirtualizedList',
	slot: 'Wrapper',
})(({theme}) => ({
	flex: '1 1 auto',
	overflow: 'hidden',
	position: 'relative',
	minHeight: 0,
}));

const VirtualizedListFooterLeftSideContent = styled('div', {
	name: 'VirtualizedListPager',
	slot: 'FooterLeftSideContent',
})(({theme}) => ({
	paddingLeft: theme.spacing(1),
	flex: '1 1 auto',
}));

/**
 * Props that will be sent to the list item(s)
 * `ItemData` - The data specificlly from the return of a server request (will be unique data for each child)\
 * `ChildProps` - Any additional data that should be passed to all children
 */
export type VirtualizedListItemProps<
	ItemData extends BaseItemData,
	ChildProps extends object,
> = Omit<ListChildComponentProps, 'data'> & {
	data: {
		rows: ItemData[];
		viewportWidth: number;
		getListItemComponentProps?: (
			index: number,
			data: ItemData,
		) => Partial<Omit<ChildProps, 'rows' | 'viewportWidth'>>;
	} & ChildProps;
};

/**
 * `ItemData` - The data specificlly from the return of a server request (will be unique data for each child)\
 * `ChildProps` - Any additional data that should be passed to all children
 */
export interface VirtualizedListProps<
	ItemData extends BaseItemData,
	ChildProps extends object,
> extends InfiniteLoaderComponentCommonProps<ItemData>,
		Partial<
			Pick<
				FixedSizeListProps,
				| 'innerElementType'
				| 'outerElementType'
				| 'overscanCount'
				| 'useIsScrolling'
				| 'style'
				| 'innerRef'
				| 'outerRef'
			>
		>,
		WithClasses<
			VirtualizedListClasses,
			{
				chunkSize?: number;
				listClassName?: string;
				disableScrollShadow?: boolean;
				onOverflownChange?: (overflown: boolean) => void;
				hidePager?: boolean;
				// TODO: c'mon, man - clean up and break this all into proper smaller components
				footerLeftSideContent?: React.ReactNode;
				isScrolling?: (scrolling: boolean) => void;
				ListItemComponent: React.ComponentType<
					VirtualizedListItemProps<ItemData, ChildProps>
				>;
				ListItemComponentProps?: Partial<
					Omit<ChildProps, 'rows' | 'viewportWidth'>
				>;
				getListItemComponentProps?: (
					index: number,
					data: ItemData,
				) => Partial<Omit<ChildProps, 'rows' | 'viewportWidth'>>;
			}
		> {}

/**
 * Virtualized list\
 * Virtualizes and loads data as needed\
 * `ItemData` - The data specificlly from the return of a server request (will be unique data for each child)\
 * `ChildProps` - Any additional data that should be passed to all children
 * @deprecated
 * @example
   <VirtualizedList<ItemData, ChildProps> {..props} />
   // if the data is all there and requires no additional fetching
   // just stub out these two methods
   <VirtualizedList<ItemData, ChildProps>
      isItemLoaded={() => true}
      loadMoreItems={async () => {}}
   />
 */
export const VirtualizedListPrimitive = <
	ItemData extends BaseItemData,
	ChildProps extends object,
>({
	className,
	classes: _classes,

	disableScrollShadow,
	isItemLoaded,
	loadMoreItems,
	scrollIndex = 0,
	onItemsRendered,
	onOverflownChange,
	onPageChange,
	onScrollIndexUpdate,
	onResize,
	data,
	dataReady,
	footerLeftSideContent,
	listClassName,
	total,
	virtualListRef,

	chunkSize,
	page = 1,
	pageSize = 25,
	hidePager,
	itemSize = 56,
	isScrolling,
	useClassicPaging = false,
	itemTotal,
	ListItemComponent,
	ListItemComponentProps,
	getListItemComponentProps,
	...props
}: VirtualizedListProps<ItemData, ChildProps>) => {
	const infiniteLoaderRef = useRef<InfiniteLoader>(null);
	const _virtualListRef = useRef<FixedSizeList | null>(null);
	const [currentPage, setCurrentPage] = useState(page || 1);
	const [overflown, setOverflown] = useState(false);
	const [stageSize, setStageSize] = useState({width: 0, height: 0});

	const lastRendered = useRef({
		overscanStartIndex: 0,
		overscanStopIndex: 0,
		visibleStartIndex: 0,
		visibleStopIndex: 0,
	});

	const classes = useUtilityClasses({
		classes: _classes,
		showBorder: disableScrollShadow,
	});

	// Store to deal with `<AutoSizer />` renders and actual size changes
	const sizeRef = useRef({height: 0, width: 0});

	const [visibleIndexes, setVisibleIndexes] = useState({
		start: 0,
		stop: 0,
	});

	if (!itemTotal) itemTotal = total;
	if (!chunkSize) chunkSize = pageSize;

	// If data is not ready, set to `chunkSize` for skeleton loading
	const totalItems = total > 0 || dataReady ? total : chunkSize;
	const pageCount = Math.ceil(totalItems / pageSize);
	const pageCountRef = useRef(pageCount);
	pageCountRef.current = pageCount;

	useEffect(() => {
		setCurrentPage(page || 1);
	}, [page]);

	useWhenChangedEffect(page, () => {
		if (!useClassicPaging) return;
		infiniteLoaderRef.current?.resetloadMoreItemsCache();
	});

	useEffect(() => {
		infiniteLoaderRef.current?.resetloadMoreItemsCache();
		_virtualListRef.current?.scrollTo(0);
	}, [dataReady]);

	useWhenChangedEffect([currentPage], () => {
		if (!useClassicPaging) return;
		_virtualListRef.current?.scrollTo(0);
	});

	const [onOverflownChangeRef] = useEnhancedRef(onOverflownChange);
	const OuterElement = React.useMemo(
		() =>
			React.forwardRef<HTMLDivElement>((props, ref) => (
				<ScrollBorderReactWindow
					ref={ref}
					{...props}
					onOverflowChange={(overflown) => {
						onOverflownChangeRef.current?.(overflown);
						setOverflown(overflown);
					}}
				/>
			)),
		[],
	);

	const handleItemsRendered = useCallback(
		(_onItemsRendered: (props: ListOnItemsRenderedProps) => void) =>
			(props: ListOnItemsRenderedProps) => {
				const {visibleStartIndex, visibleStopIndex} = props;

				lastRendered.current = props;

				handleVisiableStartIndexUpdate(visibleStartIndex, visibleStopIndex);
				_onItemsRendered(props);
				onItemsRendered?.(props);
				infiniteLoaderRef.current?.forceUpdate();
			},
		[onScrollIndexUpdate, useClassicPaging, currentPage, onItemsRendered],
	);

	const handleIsItemLoaded = useCallback(
		(index: number) => (!dataReady ? true : isItemLoaded(index)),
		[dataReady, isItemLoaded],
	);

	const handleLoadMoreItems = useCallback(
		async (startIndex: number, stopIndex: number) => {
			return !dataReady ? undefined : loadMoreItems(startIndex, stopIndex);
		},
		[dataReady, loadMoreItems],
	);

	// When page changes, scroll to the item
	const handlePageChange = useCallback(
		(event: any, page: number) => {
			_virtualListRef.current?.scrollToItem(page * pageSize, 'start');
		},
		[pageSize],
	);

	// Pass up the current visible start index
	const handleVisiableStartIndexUpdate = useCallback(
		(visibleStartIndex: number, visibleStopIndex: number) => {
			if (!useClassicPaging) {
				if (isNaN(visibleStartIndex)) return;
				const currentPage = Math.max(
					Math.ceil((visibleStartIndex + 1) / pageSize),
					1,
				);
				setCurrentPage(currentPage);
				onScrollIndexUpdate?.(visibleStartIndex, currentPage, visibleStopIndex);
			} else {
				onScrollIndexUpdate?.(visibleStartIndex, page, visibleStopIndex);
			}
			setVisibleIndexes({
				start: visibleStartIndex,
				stop: visibleStopIndex,
			});
		},
		[onScrollIndexUpdate, pageSize, useClassicPaging, page],
	);

	const scrollBarWidth = useRef(getScrollbarWidth());

	const handleResize = useCallback(
		_.debounce((width = 0, height = 0) => {
			if (
				sizeRef.current.width !== width ||
				sizeRef.current.height !== height
			) {
				sizeRef.current = {width, height};
				setStageSize({width, height});
				const subtractFromWidth = overflown ? scrollBarWidth.current : 0;
				onResize?.(width - subtractFromWidth, height);
			}
		}, 16),
		[overflown, onResize],
	);

	useWhenChangedEffect(overflown, (overflown) => {
		const subtractFromWidth = overflown ? scrollBarWidth.current : 0;
		onResize?.(
			sizeRef.current.width - subtractFromWidth,
			sizeRef.current.height,
		);
	});

	useEffect(() => {
		if (!infiniteLoaderRef.current) return;
		handleItemsRendered((infiniteLoaderRef.current as any)._onItemsRendered)(
			lastRendered.current,
		);
	}, [itemTotal, dataReady]);

	return (
		<VirtualizedListRoot
			data-testid="virtualized-list"
			className={clsx(classes.root, className)}
		>
			<ResizeDetector<HTMLDivElement>
				handleHeight
				handleWidth
				onResize={handleResize}
			>
				{({targetRef}) => (
					<VirtualizedListWrapper ref={targetRef} className={classes.wrapper}>
						<InfiniteLoader
							ref={infiniteLoaderRef}
							isItemLoaded={handleIsItemLoaded}
							itemCount={itemTotal!}
							loadMoreItems={handleLoadMoreItems}
							minimumBatchSize={chunkSize}
							threshold={chunkSize}
						>
							{({onItemsRendered, ref}) => (
								<FixedSizeList
									className={clsx('fixed-size-list', listClassName)}
									overscanCount={Math.max(chunkSize! - 10, 10)}
									itemCount={itemTotal!}
									outerElementType={OuterElement}
									{...props}
									height={stageSize.height}
									itemData={{
										rows: data,
										viewportWidth: stageSize.width,
										getListItemComponentProps,
										...ListItemComponentProps,
									}}
									itemKey={(index, data) => {
										return data.id || index;
									}}
									itemSize={itemSize}
									// Utilize the scroll index passed in to get to the right spot in the list
									initialScrollOffset={itemSize * scrollIndex}
									onItemsRendered={handleItemsRendered(onItemsRendered)}
									ref={combineRefs<FixedSizeList | null>(
										ref,
										_virtualListRef,
										virtualListRef,
									)}
									width={stageSize.width}
								>
									{ListItemComponent}
								</FixedSizeList>
							)}
						</InfiniteLoader>
					</VirtualizedListWrapper>
				)}
			</ResizeDetector>

			{!hidePager ? (
				<VirtualizedListFooter className={classes.footer}>
					<VirtualizedListFooterLeftSideContent>
						{footerLeftSideContent}
					</VirtualizedListFooterLeftSideContent>
					{useClassicPaging ? (
						<VirtualizedListClassicPager
							count={total}
							onPageChange={(event, page) => onPageChange?.(page + 1)}
							page={page - 1}
							rowsPerPage={pageSize}
							getItemAriaLabel={(type) => `Go to ${type} page`}
							showFirstButton={pageCount > 1}
							showLastButton={pageCount > 1}
							loading={!dataReady}
						/>
					) : (
						<VirtualizedListPager
							count={total}
							onPageChange={handlePageChange}
							loading={!dataReady}
							page={currentPage - 1}
							visibleStartIndex={visibleIndexes.start}
							visibleStopIndex={visibleIndexes.stop}
							rowsPerPage={pageSize}
							showFirstButton={pageCount > 1}
							showLastButton={pageCount > 1}
							getItemAriaLabel={(type) => `Go to ${type} page`}
						/>
					)}
				</VirtualizedListFooter>
			) : null}
		</VirtualizedListRoot>
	);
};

/** @deprecated */
export const VirtualizedList = React.memo(VirtualizedListPrimitive) as <
	ItemData extends BaseItemData,
	ChildProps extends object,
>(
	props: VirtualizedListProps<ItemData, ChildProps>,
) => JSX.Element;

/** @deprecated */
export default VirtualizedList;
